import { createFileRoute, useNavigate } from "@tanstack/react-router";
import { zodResolver } from "@hookform/resolvers/zod";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { Controller, useForm } from "react-hook-form";
import { useMemo, useRef, useState } from "react";
import { z } from "zod";
import { Download, Loader2, Upload, X } from "lucide-react";

import { Button } from "@/components/ui/button";
import { DatePicker } from "@/components/ui/date-picker";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Textarea } from "@/components/ui/textarea";
import { ApiError } from "@/lib/api-client";
import {
  createFeatureRequest,
  downloadBRDExample,
  downloadBRDTemplate,
  uploadBRD,
} from "@/lib/api/feature-requests";
import { cn } from "@/lib/cn";
import { DIRECTORATES, STRATEGIC_PILLARS } from "@/lib/constants";
import { formatFileSize } from "@/lib/format";
import { computeResponseDue } from "@/lib/response-cycle";

const BRD_ACCEPT = "application/pdf,.pdf";
const BRD_MAX_BYTES = 20 * 1024 * 1024;

function validateBRD(file: File): string | null {
  const ext = file.name.slice(file.name.lastIndexOf(".")).toLowerCase();
  if (ext !== ".pdf") {
    return "Attach your BRD as a PDF.";
  }
  if (file.size > BRD_MAX_BYTES) return "BRD must be at most 20 MB.";
  return null;
}

const schema = z.object({
  title: z.string().min(1, "Enter the initiative or project name.").max(140),
  directorate: z.string().min(1, "Choose the requesting directorate."),
  expected_deadline: z
    .string()
    .min(1, "Pick a go-live date from today onwards.")
    .refine((v) => v >= todayISO(), "Pick a go-live date from today onwards."),
  project_objective: z.string().min(1, "Choose the strategic pillar."),
  description: z.string().max(600).optional(),
  ceo_note: z.string().max(400).optional(),
});

/** Local today as YYYY-MM-DD (lexicographically comparable to date values). */
function todayISO(): string {
  const now = new Date();
  const local = new Date(now.getTime() - now.getTimezoneOffset() * 60_000);
  return local.toISOString().slice(0, 10);
}

function formatDecisionDay(iso: string): string {
  const [y, m, d] = iso.split("-").map(Number);
  const date = new Date(y, m - 1, d);
  return date.toLocaleDateString(undefined, {
    weekday: "short",
    day: "numeric",
    month: "short",
  });
}

type FormValues = z.infer<typeof schema>;

export const Route = createFileRoute("/_app/requests/new")({
  component: NewRequestPage,
});

function NewRequestPage() {
  const navigate = useNavigate();
  const queryClient = useQueryClient();

  const form = useForm<FormValues>({
    resolver: zodResolver(schema),
    defaultValues: {
      title: "",
      directorate: "",
      expected_deadline: "",
      project_objective: "",
      description: "",
      ceo_note: "",
    },
  });

  const [ceoHighlight, setCeoHighlight] = useState<boolean | null>(null);
  const [ceoError, setCeoError] = useState<string | null>(null);
  const [brdFile, setBrdFile] = useState<File | null>(null);
  const [brdError, setBrdError] = useState<string | null>(null);
  const [phase, setPhase] = useState<"idle" | "creating" | "uploading">("idle");
  const [downloadingTemplate, setDownloadingTemplate] = useState(false);
  const [downloadingExample, setDownloadingExample] = useState(false);
  const fileInputRef = useRef<HTMLInputElement>(null);

  const slaToday = useMemo(() => {
    const due = computeResponseDue(new Date().toISOString());
    return `Submit today (${formatDecisionDay(todayISO())}) and you'll get a decision by ${formatDecisionDay(due)}.`;
  }, []);

  const mutation = useMutation({
    mutationFn: async (input: Parameters<typeof createFeatureRequest>[0] & { file: File }) => {
      setPhase("creating");
      const created = await createFeatureRequest(input);
      setPhase("uploading");
      try {
        await uploadBRD(created.id, input.file);
      } catch (err) {
        const detail =
          err instanceof ApiError ? err.message : "BRD upload failed";
        throw new ApiError(
          `Request saved, but BRD upload failed (${detail}). Open the request and attach the BRD again.`,
          err instanceof ApiError ? err.status : 500,
          err instanceof ApiError ? err.body : undefined,
        );
      }
      return created;
    },
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: ["feature-requests"] });
      await navigate({ to: "/" });
    },
    onSettled: () => setPhase("idle"),
  });

  const onSubmit = form.handleSubmit((values) => {
    if (ceoHighlight === null) {
      setCeoError("Choose Yes or No.");
      return;
    }
    setCeoError(null);
    if (!brdFile) {
      setBrdError("Attach your BRD as a PDF.");
      return;
    }
    setBrdError(null);
    mutation.mutate({
      title: values.title.trim(),
      directorate: values.directorate,
      project_objective: values.project_objective,
      ceo_highlight: ceoHighlight,
      ceo_note: ceoHighlight ? values.ceo_note?.trim() || undefined : undefined,
      description: values.description?.trim() || undefined,
      expected_deadline: values.expected_deadline,
      file: brdFile,
    });
  });

  const pickBRD = (file: File | null) => {
    if (!file) return;
    const error = validateBRD(file);
    setBrdError(error);
    setBrdFile(error ? null : file);
  };

  const onDownloadTemplate = async () => {
    setDownloadingTemplate(true);
    try {
      await downloadBRDTemplate();
    } catch {
      /* template download is best-effort */
    } finally {
      setDownloadingTemplate(false);
    }
  };

  const onDownloadExample = async () => {
    setDownloadingExample(true);
    try {
      await downloadBRDExample();
    } catch {
      /* example download is best-effort */
    } finally {
      setDownloadingExample(false);
    }
  };

  return (
    <form
      onSubmit={onSubmit}
      onKeyDown={(e) => {
        if ((e.metaKey || e.ctrlKey) && e.key === "Enter") {
          e.preventDefault();
          void onSubmit();
        }
      }}
      className="grid min-w-0 gap-5"
    >
      <div>
        <p className="mb-1.5 text-xs font-medium tracking-widest text-primary uppercase">New initiative / feature</p>
        <h1 className="font-heading text-2xl leading-tight font-semibold tracking-tight">Request an initiative</h1>
        <p className="mt-1.5 max-w-[60ch] text-sm leading-relaxed text-muted-foreground">
          Fill in the details and attach your completed BRD. A product manager reviews every request and posts the decision on the board.
        </p>
      </div>

      <div className="grid items-start gap-10 min-[860px]:grid-cols-[minmax(0,1fr)_300px]">
        <div className="grid gap-[22px]">
          <div className="space-y-1.5">
            <Label htmlFor="title" className="text-[13px]">
              Initiative / project name <span className="text-destructive">*</span>
            </Label>
            <Input
              id="title"
              {...form.register("title")}
              placeholder="e.g. Same-day pickup for corporate accounts"
              className="bg-background font-medium"
              autoComplete="off"
              autoFocus
              maxLength={140}
            />
            {form.formState.errors.title ? (
              <p className="text-xs text-destructive">{form.formState.errors.title.message}</p>
            ) : null}
          </div>

          <div className="grid gap-5 sm:grid-cols-2">
            <div className="space-y-1.5">
              <Label htmlFor="directorate" className="text-[13px]">
                Directorate <span className="text-destructive">*</span>
              </Label>
              <Controller
                control={form.control}
                name="directorate"
                render={({ field }) => (
                  <Select value={field.value || undefined} onValueChange={field.onChange}>
                    <SelectTrigger id="directorate" className="w-full bg-background">
                      <SelectValue placeholder="Choose directorate" />
                    </SelectTrigger>
                    <SelectContent>
                      {DIRECTORATES.map((opt) => (
                        <SelectItem key={opt} value={opt}>
                          {opt}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                )}
              />
              {form.formState.errors.directorate ? (
                <p className="text-xs text-destructive">{form.formState.errors.directorate.message}</p>
              ) : null}
            </div>

            <div className="space-y-1.5">
              <Label htmlFor="expected_deadline" className="text-[13px]">
                Expected go-live date <span className="text-destructive">*</span>
              </Label>
              <Controller
                control={form.control}
                name="expected_deadline"
                render={({ field }) => (
                  <DatePicker
                    id="expected_deadline"
                    value={field.value}
                    onChange={field.onChange}
                    placeholder="Pick a date"
                    disablePast
                  />
                )}
              />
              {form.formState.errors.expected_deadline ? (
                <p className="text-xs text-destructive">{form.formState.errors.expected_deadline.message}</p>
              ) : null}
            </div>
          </div>

          <div className="space-y-2">
            <Label className="text-[13px]">
              BRD document <span className="text-destructive">*</span>
            </Label>
            <input
              ref={fileInputRef}
              type="file"
              accept={BRD_ACCEPT}
              className="sr-only"
              aria-label="Upload BRD document"
              onChange={(e) => {
                pickBRD(e.target.files?.[0] ?? null);
                e.target.value = "";
              }}
            />
            {brdFile ? (
              <div className="flex items-center gap-3.5 rounded-xl bg-muted px-[18px] py-[18px] ring-1 ring-border">
                <span className="relative flex h-[42px] w-[34px] shrink-0 items-end justify-center rounded-md bg-background pb-1 font-mono text-[9px] font-medium text-primary ring-1 ring-primary/25">
                  PDF
                </span>
                <span className="min-w-0 flex-1">
                  <span className="block truncate text-sm font-semibold">{brdFile.name}</span>
                  <span className="block text-[13px] text-muted-foreground tabular-nums">
                    {formatFileSize(brdFile.size)} · ready to upload
                  </span>
                </span>
                <Button
                  type="button"
                  variant="ghost"
                  size="icon"
                  className="shrink-0 text-muted-foreground"
                  aria-label="Remove BRD document"
                  onClick={() => {
                    setBrdFile(null);
                    setBrdError(null);
                  }}
                >
                  <X className="size-4" />
                </Button>
              </div>
            ) : (
              <button
                type="button"
                onClick={() => fileInputRef.current?.click()}
                onDragOver={(e) => e.preventDefault()}
                onDrop={(e) => {
                  e.preventDefault();
                  pickBRD(e.dataTransfer.files?.[0] ?? null);
                }}
                className="flex w-full items-center gap-3.5 rounded-xl border border-dashed border-border bg-muted px-[18px] py-[18px] text-left transition-colors hover:border-ring hover:bg-accent"
              >
                <span className="relative flex h-[42px] w-[34px] shrink-0 items-end justify-center rounded-md bg-background pb-1 font-mono text-[9px] font-medium text-primary ring-1 ring-primary/25">
                  PDF
                </span>
                <span className="min-w-0 flex-1">
                  <span className="block text-sm font-semibold">Drop your BRD here or click to browse</span>
                  <span className="block text-[13px] text-muted-foreground">
                    PDF only, up to 20 MB. Fill in the Word template, then save it as PDF.
                  </span>
                </span>
                <Upload className="size-4 shrink-0 text-muted-foreground" />
              </button>
            )}
            {brdError ? <p className="text-xs text-destructive">{brdError}</p> : null}
          </div>

          <fieldset className="space-y-2">
            <legend className="text-[13px] font-medium">
              Strategic pillar <span className="text-destructive">*</span>
            </legend>
            <p className="text-xs text-muted-foreground">
              Which company goal does this initiative mainly serve? Choose one.
            </p>
            <Controller
              control={form.control}
              name="project_objective"
              render={({ field }) => (
                <div
                  role="radiogroup"
                  aria-label="Strategic pillar"
                  className="grid gap-2 sm:grid-cols-2 min-[720px]:grid-cols-3"
                >
                  {STRATEGIC_PILLARS.map((pillar) => {
                    const active = field.value === pillar.key;
                    return (
                      <label
                        key={pillar.key}
                        className={cn(
                          "relative grid cursor-pointer gap-0.5 rounded-lg border bg-background px-3 py-2.5 pl-8 transition-colors",
                          active
                            ? "border-primary bg-primary/5"
                            : "border-border hover:border-ring",
                        )}
                      >
                        <input
                          type="radio"
                          name="project_objective"
                          value={pillar.key}
                          checked={active}
                          onChange={() => field.onChange(pillar.key)}
                          className="absolute top-3 left-2.5 accent-primary"
                        />
                        <span className="text-sm font-semibold leading-tight">{pillar.label}</span>
                        <span className="text-xs leading-snug text-muted-foreground">{pillar.help}</span>
                      </label>
                    );
                  })}
                </div>
              )}
            />
            {form.formState.errors.project_objective ? (
              <p className="text-xs text-destructive">{form.formState.errors.project_objective.message}</p>
            ) : null}
          </fieldset>

          <div className="space-y-2">
            <Label className="text-[13px]">
              CEO highlight <span className="text-destructive">*</span>
            </Label>
            <p className="-mt-1 text-xs text-muted-foreground">
              Should this initiative be flagged in the CEO update?
            </p>
            <div role="group" aria-label="CEO highlight" className="flex w-fit overflow-hidden rounded-lg ring-1 ring-border">
              {[
                { value: true, label: "Yes" },
                { value: false, label: "No" },
              ].map((opt) => {
                const active = ceoHighlight === opt.value;
                return (
                  <button
                    key={opt.label}
                    type="button"
                    onClick={() => {
                      setCeoHighlight(opt.value);
                      setCeoError(null);
                    }}
                    aria-pressed={active}
                    className={cn(
                      "flex min-w-24 items-center justify-center border-border px-5 py-2 text-sm font-medium transition-colors first:border-r",
                      active
                        ? "bg-primary text-primary-foreground"
                        : "bg-background text-foreground hover:bg-muted",
                    )}
                  >
                    {opt.label}
                  </button>
                );
              })}
            </div>
            {ceoError ? <p className="text-xs text-destructive">{ceoError}</p> : null}
          </div>

          {ceoHighlight === true ? (
            <div className="space-y-1.5">
              <Label htmlFor="ceo_note" className="text-[13px]">
                What should the CEO know?
              </Label>
              <Textarea
                id="ceo_note"
                {...form.register("ceo_note")}
                maxLength={400}
                placeholder="Two or three lines: the business impact and why it matters now."
                className="min-h-[84px] bg-background"
              />
            </div>
          ) : null}

          <div className="space-y-1.5">
            <Label htmlFor="description" className="text-[13px]">
              Notes for the PM <span className="font-normal text-muted-foreground">(optional)</span>
            </Label>
            <Textarea
              id="description"
              {...form.register("description")}
              maxLength={600}
              placeholder="e.g. Ini sudah dibahas di weekly meeting CEO"
              className="min-h-[84px] bg-background"
            />
          </div>

          {mutation.isError ? (
            <p className="text-sm font-medium text-destructive">
              {mutation.error instanceof ApiError
                ? mutation.error.message
                : "Failed to create request"}
            </p>
          ) : null}

          <div className="flex flex-wrap items-center gap-3.5 border-t border-border pt-[18px]">
            <Button type="submit" size="lg" disabled={mutation.isPending}>
              {mutation.isPending ? <Loader2 className="size-4 animate-spin" /> : null}
              {phase === "uploading" ? "Uploading BRD…" : mutation.isPending ? "Submitting…" : "Submit request"}
            </Button>
          </div>
        </div>

        <aside className="grid gap-3.5 self-start rounded-xl bg-card p-5 shadow-xs ring-1 ring-foreground/10">
          <h2 className="font-heading text-lg font-semibold tracking-tight">How it works</h2>
          <ol className="grid list-decimal gap-2.5 pl-5 text-sm leading-relaxed marker:font-mono marker:text-primary">
            <li className="grid justify-items-start gap-2">
              <span>Download the BRD template (see the example if you need help), fill it in and save it as PDF.</span>
              <div className="flex flex-wrap gap-2">
                <Button
                  type="button"
                  variant="outline"
                  size="sm"
                  onClick={onDownloadTemplate}
                  disabled={downloadingTemplate}
                >
                  {downloadingTemplate ? <Loader2 className="size-4 animate-spin" /> : <Download className="size-4" />}
                  BRD template (.docx)
                </Button>
                <Button
                  type="button"
                  variant="outline"
                  size="sm"
                  onClick={onDownloadExample}
                  disabled={downloadingExample}
                >
                  {downloadingExample ? <Loader2 className="size-4 animate-spin" /> : <Download className="size-4" />}
                  BRD example (.docx)
                </Button>
              </div>
            </li>
            <li>Submit this form with your BRD PDF attached.</li>
            <li>Check the Request board for the PM&apos;s decision.</li>
          </ol>

          <div className="grid gap-2.5 border-t border-border pt-3.5">
            <p className="font-semibold">When you&apos;ll get a decision</p>
            <table className="w-full text-[13.5px]">
              <thead>
                <tr className="text-left text-xs text-muted-foreground">
                  <th className="py-1.5 pr-2 font-medium">Submitted</th>
                  <th className="py-1.5 pr-2 font-medium">PM review</th>
                  <th className="py-1.5 font-medium">Decision by</th>
                </tr>
              </thead>
              <tbody>
                <tr className="border-t border-border">
                  <td className="py-2 pr-2">Mon – Tue</td>
                  <td className="py-2 pr-2">Wed – Fri</td>
                  <td className="py-2 font-semibold">Fri, same week</td>
                </tr>
                <tr className="border-t border-border">
                  <td className="py-2 pr-2">Wed – Sun</td>
                  <td className="py-2 pr-2">Mon – Wed</td>
                  <td className="py-2 font-semibold">Wed, next week</td>
                </tr>
              </tbody>
            </table>
            <p className="rounded-md bg-primary/10 px-2.5 py-2 text-[13.5px] font-semibold text-primary">
              {slaToday}
            </p>
            <dl className="grid gap-1.5 text-[13px]">
              <div className="grid grid-cols-[70px_minmax(0,1fr)] gap-2">
                <dt className="font-bold">Proceed</dt>
                <dd className="text-muted-foreground">Approved. It moves into planning.</dd>
              </div>
              <div className="grid grid-cols-[70px_minmax(0,1fr)] gap-2">
                <dt className="font-bold">Clarify</dt>
                <dd className="text-muted-foreground">
                  The PM needs more information. Read the reason, update your BRD and reply.
                </dd>
              </div>
              <div className="grid grid-cols-[70px_minmax(0,1fr)] gap-2">
                <dt className="font-bold">Defer</dt>
                <dd className="text-muted-foreground">
                  Valid, but not now. The reason says when it will be revisited.
                </dd>
              </div>
              <div className="grid grid-cols-[70px_minmax(0,1fr)] gap-2">
                <dt className="font-bold">Decline</dt>
                <dd className="text-muted-foreground">Not going ahead. The reason explains why.</dd>
              </div>
            </dl>
          </div>
        </aside>
      </div>
    </form>
  );
}
